import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import { useState } from "react";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetMembershipsQuery } from "@lending/hooks/queries/MembershipShare/useGetMembershipsQuery";
import { useSelectMembershipQuery } from "@lending/hooks/queries/MembershipShare/useSelectMembershipQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useProductType } from "@lending/hooks/useProductType";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { ExistingMembershipOption, NewMembershipOption } from "./components";
import styles from "./Select.module.css";
import { useCreateMembershipAccountApplication } from "../Activate/hooks/useCreateMembershipAccountApplication";
import { useMembershipShareProduct } from "../Activate/hooks/useMembershipShareProduct";
import { navigateToMembershipShareStartStep } from "../hooks/navigateToMembershipShareStartStep";
import type { Institution, Membership, MembershipRequirementResponse } from "@lending/types";

type MembershipSelectionOption = "existing" | "new" | null;

type MembershipsResponse = Membership[] | { results?: Membership[] } | undefined;
type ExistingCoreUserApplicantHandling = Institution["cao_existing_core_user_applicant_handling"];

function allowCreateNewMembershipOption(
  existingCoreUserApplicantHandling: ExistingCoreUserApplicantHandling
) {
  return existingCoreUserApplicantHandling !== "USER_ASSOCIATE";
}

function Select() {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const membershipsQuery = useGetMembershipsQuery();
  const lendingApplicationID = useLendingApplicationID();
  const applicationType = useProductType();
  const { goToPath } = useFlowNavigation();
  const { showErrorToast } = useToastsContext();
  const { membershipShareProduct } = useMembershipShareProduct();
  const { createMembershipAccountApplication, isPending: isCreatingMembershipAccountApplication } =
    useCreateMembershipAccountApplication();
  const [selectedOption, setSelectedOption] = useState<MembershipSelectionOption>(null);
  const membershipsData = membershipsQuery.data as MembershipsResponse;
  const memberships: Membership[] = Array.isArray(membershipsData)
    ? membershipsData
    : (membershipsData?.results ?? []);
  const [selectedMembershipCoreUserId, setSelectedMembershipCoreUserId] = useState<string | null>(
    null
  );
  const singleMembership = memberships.length === 1 ? memberships[0] : undefined;
  const selectedExistingMembership = memberships.find(
    (membership) => membership.core_user_id === selectedMembershipCoreUserId
  );
  const showCreateNewMembershipOption = allowCreateNewMembershipOption(
    institution.cao_existing_core_user_applicant_handling
  );
  const membershipShareBasePath = lendingApplicationID
    ? `/${applicationType}/${lendingApplicationID}/membership-share`
    : null;

  const selectMembershipMutation = useSelectMembershipQuery({
    onSuccess: async ({
      state,
      membership_share_verification_required,
      membership_share_email_conflict,
    }: MembershipRequirementResponse) => {
      if (!applicationType || !lendingApplicationID) {
        return;
      }

      if (state === "membership_share_pending") {
        if (selectedOption === "new") {
          if (membership_share_email_conflict) {
            if (!membershipShareBasePath) {
              return;
            }

            goToPath(`${membershipShareBasePath}/use-different-email`);
            return;
          }

          const membershipAccountApplicationResult = await createMembershipAccountApplication();

          if (membershipAccountApplicationResult.status === "success") {
            if (!membershipShareBasePath) {
              return;
            }

            goToPath(`${membershipShareBasePath}/disclosures`);
            return;
          }

          if (membershipAccountApplicationResult.status === "authentication_required") {
            await navigateToMembershipShareStartStep({
              applicationType,
              lendingApplicationID,
              state,
              verificationRequired: true,
              goToPath,
            });
            return;
          }

          if (membershipAccountApplicationResult.status === "email_conflict") {
            if (!membershipShareBasePath) {
              return;
            }

            goToPath(`${membershipShareBasePath}/use-different-email`);
          }

          return;
        }

        await navigateToMembershipShareStartStep({
          applicationType,
          lendingApplicationID,
          state,
          verificationRequired: membership_share_verification_required,
          emailConflict: membership_share_email_conflict,
          goToPath,
        });
        return;
      }

      goToPath(`/${applicationType}/${lendingApplicationID}/submitted`);
    },
  });

  function onSelectExistingMembershipOption() {
    setSelectedOption("existing");

    if (memberships.length === 1) {
      setSelectedMembershipCoreUserId(memberships[0].core_user_id);
    }
  }

  if (membershipsQuery.isError) {
    return <ErrorScreen />;
  }

  async function onContinue() {
    if (selectedOption === null) {
      showErrorToast(
        l10n.getString(
          "membership-share-select-option-required",
          null,
          "Select an option to continue."
        )
      );
      return;
    }

    if (selectedOption === "existing") {
      const coreUserId = singleMembership?.core_user_id ?? selectedMembershipCoreUserId;

      if (!coreUserId) {
        showErrorToast(
          l10n.getString(
            "membership-share-select-membership-required",
            null,
            "Select a membership to continue."
          )
        );
        return;
      }

      await selectMembershipMutation.selectMembership({ core_user_id: coreUserId });
      return;
    }

    if (selectedOption === "new") {
      await selectMembershipMutation.selectMembership({ create_new_membership: true });
    }
  }

  return (
    <PageLayout className={styles.pageLayout} isLoading={membershipsQuery.isLoading}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className={styles.heading}>
              {l10n.getString("membership-share-select-heading", null, "Activate your membership")}
            </h1>
            <div className={styles.subheading}>
              {l10n.getString(
                "membership-share-select-subheading",
                { institutionName: institution.short_name || "" },
                "We found an existing membership under your name. You can use it for this loan, or open a new membership account."
              )}{" "}
              {membershipShareProduct?.more_details_url ? (
                <a
                  href={membershipShareProduct.more_details_url}
                  className={styles.learnMoreLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {l10n.getString("membership-share-select-learn-more", null, "Learn more")}
                </a>
              ) : null}
            </div>
          </div>

          <div className={styles.options}>
            <ExistingMembershipOption
              selected={selectedOption === "existing"}
              label={l10n.getString(
                "membership-share-select-existing-option",
                null,
                "Use my existing member ID"
              )}
              memberships={memberships}
              selectedMembership={selectedExistingMembership ?? singleMembership}
              selectedMembershipCoreUserId={selectedMembershipCoreUserId}
              onSelectOption={onSelectExistingMembershipOption}
              onSelectMembership={setSelectedMembershipCoreUserId}
            />

            {showCreateNewMembershipOption ? (
              <NewMembershipOption
                selected={selectedOption === "new"}
                label={l10n.getString(
                  "membership-share-select-new-option",
                  null,
                  "Create a new member ID"
                )}
                onSelectOption={() => setSelectedOption("new")}
              />
            ) : null}
          </div>

          <div className={styles.footer}>
            <Button
              label={l10n.getString("button-label-continue", null, "Continue")}
              isLoading={
                selectMembershipMutation.isPending || isCreatingMembershipAccountApplication
              }
              onClick={() => void onContinue()}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { Select };
